import React, { useMemo, useState } from "react";
import "./ComparisonTable.css";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { TablePagination } from "@mui/material";

const CRITICAL_STATUSES = ["НЕТ В УЧЕТЕ", "НЕТ В БАНКЕ", "РАЗНИЦА"];

const WARNING_STATUSES = [
    "РАЗНЫЕ СЧЕТА",
    "РАЗНОЕ РАСПРЕДЕЛЕНИЕ СУММ",
    "НЕСКОЛЬКО СЧЕТОВ",
    "РАЗНЫЕ ФИО",
    "НЕСКОЛЬКО ФИО",
];

const isCriticalRow = (row) => CRITICAL_STATUSES.includes(row["Результат"]);

const isWarningRow = (row) =>
    WARNING_STATUSES.includes(row["Счет_результат"]) ||
    WARNING_STATUSES.includes(row["ФИО_результат"]);

const getRowClass = (row) => {
    if (isCriticalRow(row)) return "row-critical";
    if (isWarningRow(row)) return "row-warning";
    return "row-ok";
};

const getPriority = (row) => {
    if (isCriticalRow(row)) return 0;
    if (isWarningRow(row)) return 1;
    return 2;
};

const renderListOrSingle = (arr) => {
    if (!arr || arr.length === 0) return "";
    if (arr.length === 1) return arr[0];

    return (
        <ul className="cell-list">
            {arr.map((val, i) => (
                <li key={i}>{val}</li>
            ))}
        </ul>
    );
};

const formatNumber = (value) => Number(value || 0).toLocaleString("ru-RU");

function ComparisonTable({ data = [], totals }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const p1 = getPriority(a);
            const p2 = getPriority(b);

            if (p1 !== p2) return p1 - p2;

            const fioA = (a["ФИО_pdf_финал"] || a["ФИО_1С_финал"] || [""])[0] || "";
            const fioB = (b["ФИО_pdf_финал"] || b["ФИО_1С_финал"] || [""])[0] || "";

            return fioA.localeCompare(fioB, "ru");
        });
    }, [data]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const criticalCount = useMemo(() => {
        return sortedData.filter(isCriticalRow).length;
    }, [sortedData]);

    const warningCount = useMemo(() => {
        return sortedData.filter((row) => !isCriticalRow(row) && isWarningRow(row)).length;
    }, [sortedData]);

    const okCount = useMemo(() => {
        return sortedData.length - criticalCount - warningCount;
    }, [sortedData.length, criticalCount, warningCount]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Сравнение");

        worksheet.columns = [
            { key: "iin_pdf", width: 16 },
            { key: "fio_pdf", width: 32 },
            { key: "accounts_pdf", width: 34 },
            { key: "sum_pdf", width: 16 },
            { key: "sum_pdf_breakdown", width: 28 },

            { key: "iin_1c", width: 16 },
            { key: "fio_1c", width: 32 },
            { key: "accounts_1c", width: 34 },
            { key: "sum_1c", width: 16 },
            { key: "sum_1c_breakdown", width: 28 },

            { key: "diff", width: 16 },
            { key: "status", width: 18 },
            { key: "account_status", width: 22 },
            { key: "fio_status", width: 18 },
        ];

        worksheet.getCell("A1").value = "ИТОГО:";
        worksheet.getCell("A2").value = "ИИН:";
        worksheet.getCell("B2").value = totals?.total_iins ?? 0;

        worksheet.getCell("A3").value = "Банк:";
        worksheet.getCell("B3").value = totals?.total_pdf ?? 0;

        worksheet.getCell("A4").value = "Учет:";
        worksheet.getCell("B4").value = totals?.total_1c ?? 0;

        worksheet.getCell("A5").value = "Общая разница:";
        worksheet.getCell("B5").value = totals?.total_difference ?? 0;

        ["A1", "A2", "A3", "A4", "A5"].forEach((cellRef) => {
            worksheet.getCell(cellRef).font = { bold: true };
            worksheet.getCell(cellRef).alignment = { horizontal: "left" };
        });

        ["B2", "B3", "B4", "B5"].forEach((cellRef) => {
            worksheet.getCell(cellRef).alignment = { horizontal: "left" };
        });

        ["B3", "B4", "B5"].forEach((cellRef) => {
            worksheet.getCell(cellRef).numFmt = "#,##0.00";
        });

        const headerRowNumber = 8;
        const headerRow = worksheet.getRow(headerRowNumber);

        headerRow.values = [
            "ИИН (банк)",
            "ФИО (банк)",
            "Счета (банк)",
            "Сумма (банк)",
            "Расшифровка сумм (банк)",
            "ИИН (учет)",
            "ФИО (учет)",
            "Счета (учет)",
            "Сумма (учет)",
            "Расшифровка сумм (учет)",
            "Разница",
            "Статус",
            "Статус счетов",
            "Статус ФИО",
        ];

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "1F4E78" },
            };
            cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true,
            };
        });

        headerRow.height = 30;

        sortedData.forEach((row) => {
            const excelRow = worksheet.addRow([
                row["ИИН_pdf"] || "",
                (row["ФИО_pdf_финал"] || []).join(" | "),
                (row["Счета_pdf_финал"] || []).join(" | "),
                Number(row["Сумма_pdf"] || 0),
                row["Сумма_pdf_расш"] && row["Сумма_pdf_расш"].length > 1
                    ? `(Σ ${row["Сумма_pdf_расш"]
                          .map((s) => Number(s).toLocaleString("ru-RU"))
                          .join(", ")})`
                    : "",

                row["ИИН_1С"] || "",
                (row["ФИО_1С_финал"] || []).join(" | "),
                (row["Счета_1С_финал"] || []).join(" | "),
                Number(row["Сумма_1С"] || 0),
                row["Сумма_1С_расш"] && row["Сумма_1С_расш"].length > 1
                    ? `(Σ ${row["Сумма_1С_расш"]
                          .map((s) => Number(s).toLocaleString("ru-RU"))
                          .join(", ")})`
                    : "",

                Number(row["Сумма_разн"] || 0),
                row["Результат"] || "",
                row["Счет_результат"] || "",
                row["ФИО_результат"] || "",
            ]);

            [4, 9, 11].forEach((colIdx) => {
                excelRow.getCell(colIdx).numFmt = "#,##0.00";
            });

            excelRow.eachCell((cell) => {
                cell.alignment = {
                    vertical: "top",
                    wrapText: true,
                };
            });

            const resultStatus = row["Результат"];
            const accountStatus = row["Счет_результат"];
            const fioStatus = row["ФИО_результат"];

            const diffCell = excelRow.getCell(11);
            const statusCell = excelRow.getCell(12);
            const accountStatusCell = excelRow.getCell(13);
            const fioStatusCell = excelRow.getCell(14);

            if (CRITICAL_STATUSES.includes(resultStatus)) {
                statusCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FDECEC" },
                };
                diffCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FDECEC" },
                };
            }

            if (WARNING_STATUSES.includes(accountStatus)) {
                accountStatusCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF8E1" },
                };
            }

            if (WARNING_STATUSES.includes(fioStatus)) {
                fioStatusCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF8E1" },
                };
            }
        });

        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber >= headerRowNumber) {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin", color: { argb: "D9D9D9" } },
                        left: { style: "thin", color: { argb: "D9D9D9" } },
                        bottom: { style: "thin", color: { argb: "D9D9D9" } },
                        right: { style: "thin", color: { argb: "D9D9D9" } },
                    };
                });
            }
        });

        worksheet.autoFilter = {
            from: { row: headerRowNumber, column: 1 },
            to: { row: headerRowNumber, column: 14 },
        };

        worksheet.views = [
            {
                state: "frozen",
                ySplit: headerRowNumber,
            },
        ];

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const now = new Date();
        const stamp =
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
                now.getDate()
            ).padStart(2, "0")}` +
            `_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(
                2,
                "0"
            )}`;

        saveAs(blob, `comparison_${stamp}.xlsx`);
    };

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <section className="comparison-section">
            <div className="comparison-header-card">
                <div className="comparison-header-main">
                    <div>
                        <div className="comparison-kicker">Результаты</div>
                        <h3 className="comparison-title">Сравнение банка и учета</h3>
                    </div>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="comparison-export-btn"
                    >
                        Скачать Excel
                    </button>
                </div>

                {totals && (
                    <div className="comparison-summary-grid">
                        <div className="summary-card">
                            <span className="summary-label">ИИН</span>
                            <strong>{formatNumber(totals.total_iins)}</strong>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Банк</span>
                            <strong>{formatNumber(totals.total_pdf)}</strong>
                        </div>
                        <div className="summary-card">
                            <span className="summary-label">Учет</span>
                            <strong>{formatNumber(totals.total_1c)}</strong>
                        </div>
                        <div className="summary-card summary-card-accent">
                            <span className="summary-label">Общая разница</span>
                            <strong>{formatNumber(totals.total_difference)}</strong>
                        </div>
                    </div>
                )}

                <div className="comparison-legend">
                    <span className="legend-chip legend-critical">
                        Критические: {criticalCount}
                    </span>
                    <span className="legend-chip legend-warning">
                        Предупреждения: {warningCount}
                    </span>
                    <span className="legend-chip legend-ok">ОК: {okCount}</span>
                </div>
            </div>

            <div className="comparison-table-card">
                <div className="comparison-table-scroll">
                    <table className="comparison-table comparison-table-accounting">
                        <thead>
                            <tr>
                                <th>
                                    ИИН <span className="sub-label">(банк)</span>
                                </th>
                                <th>
                                    ФИО <span className="sub-label">(банк)</span>
                                </th>
                                <th>
                                    Счета <span className="sub-label">(банк)</span>
                                </th>
                                <th>
                                    Сумма <span className="sub-label">(банк)</span>
                                </th>
                                <th>
                                    Расшифровка <span className="sub-label">(банк)</span>
                                </th>

                                <th>
                                    ИИН <span className="sub-label">(учет)</span>
                                </th>
                                <th>
                                    ФИО <span className="sub-label">(учет)</span>
                                </th>
                                <th>
                                    Счета <span className="sub-label">(учет)</span>
                                </th>
                                <th>
                                    Сумма <span className="sub-label">(учет)</span>
                                </th>
                                <th>
                                    Расшифровка <span className="sub-label">(учет)</span>
                                </th>

                                <th>Разница</th>
                                <th>Статус</th>
                                <th>Статус счетов</th>
                                <th>Статус ФИО</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedData.map((row, idx) => (
                                <tr key={idx} className={getRowClass(row)}>
                                    <td>{row["ИИН_pdf"] || ""}</td>
                                    <td>{renderListOrSingle(row["ФИО_pdf_финал"])}</td>
                                    <td>{renderListOrSingle(row["Счета_pdf_финал"])}</td>
                                    <td className="num">{formatNumber(row["Сумма_pdf"])}</td>
                                    <td>
                                        {row["Сумма_pdf_расш"] &&
                                        row["Сумма_pdf_расш"].length > 1 ? (
                                            <span className="sum-breakdown">
                                                (Σ{" "}
                                                {row["Сумма_pdf_расш"]
                                                    .map((s) => Number(s).toLocaleString("ru-RU"))
                                                    .join(", ")}
                                                )
                                            </span>
                                        ) : (
                                            ""
                                        )}
                                    </td>

                                    <td>{row["ИИН_1С"] || ""}</td>
                                    <td>{renderListOrSingle(row["ФИО_1С_финал"])}</td>
                                    <td>{renderListOrSingle(row["Счета_1С_финал"])}</td>
                                    <td className="num">{formatNumber(row["Сумма_1С"])}</td>
                                    <td>
                                        {row["Сумма_1С_расш"] &&
                                        row["Сумма_1С_расш"].length > 1 ? (
                                            <span className="sum-breakdown">
                                                (Σ{" "}
                                                {row["Сумма_1С_расш"]
                                                    .map((s) => Number(s).toLocaleString("ru-RU"))
                                                    .join(", ")}
                                                )
                                            </span>
                                        ) : (
                                            ""
                                        )}
                                    </td>

                                    <td className="num">{formatNumber(row["Сумма_разн"])}</td>
                                    <td>{row["Результат"] || ""}</td>
                                    <td>{row["Счет_результат"] || ""}</td>
                                    <td>{row["ФИО_результат"] || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="comparison-pagination-wrap">
                    <TablePagination
                        component="div"
                        count={sortedData.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Строк на странице:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                    />
                </div>
            </div>
        </section>
    );
}

export default ComparisonTable;